import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import ExportCSVButton from "../../../../components/buttons/ExportCSVButton";
import { Toast } from "../../../../components/Toast";
import { useFileNaming } from "../../../../hooks/useFileNaming";

function ExportMitreButton({ techniques, showRisk = false }) {
  const [isExporting, setIsExporting] = useState(false);
  const { trackEvent } = useAPI();
  const { generateCsvFileName } = useFileNaming();
  const calculateRisk = (coverageValue) => {
    if (
      coverageValue === 0 ||
      coverageValue === null ||
      coverageValue === undefined
    ) {
      return 0;
    }
    return 100 - coverageValue;
  };

  function formatTechniqueForExport(technique, tacticName) {
    const risk = calculateRisk(technique.percentage);

    return {
      Tactic: tacticName,
      "Technique ID": technique.id,
      "Technique Name": technique.name,
      "Coverage (%)": technique.percentage,
      "Risk (%)": risk,
      "Threat Actors":
        technique.related_attack_groups
          ?.map((group) => group.name)
          .join(", ") || "",
      "Threat Types": technique.related_threat_types?.join(", ") || "",
    };
  }

  const generateMultiSheetCSV = (tactics) => {
    if (!tactics || tactics.length === 0) return "";

    const headers = [
      "Tactic",
      "Technique ID",
      "Technique Name",
      "Coverage (%)",
      "Risk (%)",
      "Threat Actors",
      "Threat Types",
    ];

    const headerRow = headers.join(",");
    const sections = [];

    // Add overall summary section with all techniques
    const allTechniques = tactics.flatMap((tactic) =>
      tactic.techniques
        .filter((technique) => technique.applicable)
        .map((technique) => formatTechniqueForExport(technique, tactic.title))
    );

    sections.push(
      "ALL TECHNIQUES\n" +
        headerRow +
        "\n" +
        allTechniques.map(formatRowToCSV).join("\n")
    );

    // Add individual tactic sections
    tactics.forEach((tactic) => {
      if (
        tactic.techniques &&
        tactic.techniques.filter((technique) => technique.applicable).length > 0
      ) {
        const tacticTechniques = tactic.techniques
          .filter((technique) => technique.applicable)
          .map((technique) =>
            formatTechniqueForExport(technique, tactic.title)
          );

        sections.push(
          `\n\n${tactic.title.toUpperCase()}\n` +
            headerRow +
            "\n" +
            tacticTechniques.map(formatRowToCSV).join("\n")
        );
      }
    });

    return sections.join("\n");
  };

  function formatRowToCSV(rowData) {
    return Object.values(rowData)
      .map((value) => {
        if (value === null || value === undefined) return '""';
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",");
  }

  async function handleExport() {
    trackEvent(
      "User click on 'Export' button to download the MITRE ATT&CK techniques table"
    );
    if (!techniques || techniques.length === 0) {
      Toast.error("No techniques available to export");
      return;
    }

    setIsExporting(true);
    try {
      const csvContent = generateMultiSheetCSV(techniques);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      // Create a link element and trigger download
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", generateCsvFileName("mitre_attack_view"));
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      Toast.error(
        "Failed to export MITRE ATT&CK techniques, Please try again later"
      );
      trackEvent("Failed to export MITRE ATT&CK techniques", {
        error: error.message,
      });
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <ExportCSVButton
      handleExport={handleExport}
      disabled={isExporting || !techniques || techniques.length === 0}
    />
  );
}

export default ExportMitreButton;
